import React, { useState } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
  StyleSheet,
  Image,
  Alert,
} from 'react-native';
import { SavedCard } from '~types/api/event';
import { useTheme } from '~contexts/ThemeContext';
import closeIcon from '~assets/images/closeicon.png';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import Ionicons from 'react-native-vector-icons/Ionicons';

interface SavedCardsModalProps {
  visible: boolean;
  onClose: () => void;
  cards: SavedCard[];
  isLoading: boolean;
  onSelectCard: (card: SavedCard) => void;
  onAddNewCard?: () => void;
}

// Helper function to get card brand icon name
const getCardIconName = (brand: string): string => {
  const lowerBrand = brand.toLowerCase();
  
  if (lowerBrand.includes('visa')) {
    return 'credit-card';
  } else if (lowerBrand.includes('mastercard') || lowerBrand.includes('master card')) {
    return 'credit-card';
  } else if (lowerBrand.includes('amex') || lowerBrand.includes('american express')) {
    return 'credit-card';
  } else if (lowerBrand.includes('discover')) {
    return 'credit-card';
  } else if (lowerBrand.includes('diners')) {
    return 'credit-card';
  }
  return 'credit-card';
};

// Helper function to get card brand color
const getCardBrandColor = (brand: string): string => {
  const lowerBrand = brand.toLowerCase();
  
  if (lowerBrand.includes('visa')) {
    return '#1434CB'; // Visa blue
  } else if (lowerBrand.includes('mastercard') || lowerBrand.includes('master card')) {
    return '#FF5F00'; // Mastercard orange
  } else if (lowerBrand.includes('amex') || lowerBrand.includes('american express')) {
    return '#006FCF'; // Amex blue
  } else if (lowerBrand.includes('discover')) {
    return '#FF6000'; // Discover orange
  } else if (lowerBrand.includes('diners')) {
    return '#0079BE'; // Diners blue
  }
  return '#6B7280'; // Gray default
};

export const SavedCardsModal: React.FC<SavedCardsModalProps> = ({
  visible,
  onClose,
  cards,
  isLoading,
  onSelectCard,
  onAddNewCard,
}) => {
  const { colors } = useTheme();
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);

  const handleSelectCard = (card: SavedCard) => {
    setSelectedCardId(card.id);
    onSelectCard(card);
    onClose();
  };

  const renderCardItem = ({ item }: { item: SavedCard }) => {
    const cardColor = getCardBrandColor(item.card_brand);
    const isSelected = selectedCardId === item.id;

    return (
      <TouchableOpacity
        style={[
          styles.cardItemContainer,
          { 
            borderColor: isSelected ? colors.primary : colors.border,
            borderWidth: isSelected ? 2 : 1,
            backgroundColor: colors.cardBackground,
          },
        ]}
        onPress={() => handleSelectCard(item)}
        activeOpacity={0.7}
      >
        {/* Card Gradient Background Effect */}
        <View
          style={[
            styles.cardGradientBg,
            { backgroundColor: cardColor + '10' },
          ]}
        />

        {/* Card Content */}
        <View style={styles.cardItemContent}>
          {/* Left Side: Icon & Details */}
          <View style={styles.cardLeftSection}>
            {/* Card Brand Icon */}
            <View
              style={[
                styles.cardIconContainer,
                { backgroundColor: cardColor + '20' },
              ]}
            >
              <MaterialCommunityIcons
                name={getCardIconName(item.card_brand)}
                size={24}
                color={cardColor}
              />
            </View>

            {/* Card Details */}
            <View style={styles.cardDetails}>
              <Text style={[styles.cardBrand, { color: colors.text }]}>
                {item.card_brand.toUpperCase()}
              </Text>
              <Text style={[styles.cardNumber, { color: colors.textSecondary }]}>
                •••• •••• •••• {item.card_last4}
              </Text>
              <View style={styles.cardMetaContainer}>
                <Ionicons
                  name="calendar-outline"
                  size={12}
                  color={colors.textSecondary}
                  style={{ marginRight: 4 }}
                />
                <Text style={[styles.cardExpiry, { color: colors.textSecondary }]}>
                  {item.card_exp_month}/{item.card_exp_year}
                </Text>
                {item.is_default && (
                  <>
                    <Text style={[styles.divider, { color: colors.border }]}>
                      {' '}•{' '}
                    </Text>
                    <MaterialCommunityIcons
                      name="star"
                      size={12}
                      color={colors.primary}
                      style={{ marginLeft: 2 }}
                    />
                    <Text
                      style={[
                        styles.defaultBadge,
                        { color: colors.primary, marginLeft: 4 },
                      ]}
                    >
                      Default
                    </Text>
                  </>
                )}
              </View>
            </View>
          </View>

          {/* Right Side: Selection Indicator */}
          {isSelected && (
            <View
              style={[
                styles.selectedCheckmark,
                { backgroundColor: colors.primary },
              ]}
            >
              <Ionicons name="checkmark" size={20} color="white" />
            </View>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={[styles.headerTitle, { color: colors.text }]}>
            Select Payment Method
          </Text>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <Image source={closeIcon} style={styles.closeIcon} />
          </TouchableOpacity>
        </View>

        {/* Content */}
        {isLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        ) : cards.length > 0 ? (
          <>
            <FlatList
              data={cards}
              renderItem={renderCardItem}
              keyExtractor={(item) => item.id}
              scrollEnabled={true}
              style={styles.cardsList}
              nestedScrollEnabled={true}
            />
            <TouchableOpacity
              style={[styles.addNewCardButton, { borderColor: colors.primary }]}
              onPress={onAddNewCard}
              activeOpacity={0.7}
            >
              <MaterialCommunityIcons
                name="plus-circle"
                size={20}
                color={colors.primary}
              />
              <Text style={[styles.addNewCardText, { color: colors.primary }]}>
                Add New Card
              </Text>
            </TouchableOpacity>
          </>
        ) : (
          <View style={styles.emptyContainer}>
            <Text style={[styles.emptyText, { color: colors.text }]}>
              No saved cards found
            </Text>
            <TouchableOpacity
              style={[styles.addCardButton, { backgroundColor: colors.primary }]}
              onPress={onAddNewCard}
            >
              <Text style={styles.addCardButtonText}>Add Payment Method</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: 60,
    paddingHorizontal: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  closeButton: {
    padding: 8,
  },
  closeIcon: {
    width: 24,
    height: 24,
  },
  cardsList: {
    marginBottom: 20,
  },
  cardItemContainer: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 0,
    marginBottom: 12,
    overflow: 'hidden',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
  },
  cardGradientBg: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '100%',
    opacity: 0.05,
  },
  cardItemContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
  },
  cardLeftSection: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  cardIconContainer: {
    width: 50,
    height: 50,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  cardDetails: {
    flex: 1,
  },
  cardBrand: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 4,
    letterSpacing: 0.5,
  },
  cardNumber: {
    fontSize: 13,
    fontWeight: '500',
    marginBottom: 6,
    letterSpacing: 1,
  },
  cardMetaContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  cardExpiry: {
    fontSize: 12,
    fontWeight: '500',
  },
  divider: {
    fontSize: 10,
  },
  defaultBadge: {
    fontSize: 11,
    fontWeight: '600',
  },
  selectedCheckmark: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 12,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 16,
    marginBottom: 20,
    textAlign: 'center',
  },
  addCardButton: {
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 12,
  },
  addCardButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  addNewCardButton: {
    borderWidth: 1.5,
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
    marginVertical: 20,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  addNewCardText: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
    marginLeft: 8,
  },
});
